import Vue from 'vue'
import moment from 'moment'
import isNumber from './is-number'
import i18n from '@/scripts/app-configs/i18n-config'
import translateNumericMoth from './translate-numeric-month'

/**
 * Date utils filter
 * @name Date Utils filter
 * @returns {*} Uppercase string
 */

const DEFAULT_WHEN_NO_DATA_AVAILABLE = '-'
const DEFAULT_INPUT_DATE_FORMAT = 'DD-MMM-YYYY HH:mm A'
const DEFAULT_DURATION_STRING_FORMAT = 'd:h:m'

/**
 * Util function to form reference dates.
 */
function makeDate(reference, inputFormat, utc) {
  let date

  if (reference === 'current') {
    date = new Date()

    return date
  }

  switch (typeof reference) {
    case 'object': // Spec data object is a timestamp
      date = reference
      break
    case 'number': // Reference is a timestamp
      date = new Date(reference * 1000) // make it js friendly
      break
    default:
      // Reference is a string
      date = (utc ? moment(reference, inputFormat) : moment(reference, inputFormat).toDate())
      break
  }

  return date
}

function makeDateString(date, outputFormat) {
  const dateString = moment(date).format(outputFormat)
  let regex = /MMM/

  if (regex.test(outputFormat) === false) {
    return dateString
  }

  const monthNameObj = translateNumericMoth(date.getMonth() + 1)

  if (!monthNameObj) {
    return null
  }
  regex = /MMMM/
  if (regex.test(outputFormat)) {
    // long month name
    return dateString.replace(monthNameObj.name, monthNameObj.translatedname)
  }

  // short month name
  return dateString.replace(
    monthNameObj.name.substring(0, 3),
    monthNameObj.translatedname.substring(0, 3),
  )
}

function splitOutputFormat(outputFormat = DEFAULT_DURATION_STRING_FORMAT) {

  const showDays = /d/
  const showHours = /h/
  const showMinutes = /m/
  const showSeconds = /s/

  return {
    showDays: showDays.test(outputFormat),
    showHours: showHours.test(outputFormat),
    showMinutes: showMinutes.test(outputFormat),
    showSeconds: showSeconds.test(outputFormat),
  }
}

function splitDuration(seconds, { showDays = false, showHours = true, showMinutes = true, showSeconds = true }) {

  let currentSecs = seconds

  const days = Math.floor(currentSecs / (3600 * 24))

  currentSecs -= days * 3600 * 24

  let hrs = Math.floor(currentSecs / 3600)

  hrs += days && !showDays ? days * 24 : 0
  currentSecs -= hrs * 3600
  currentSecs += days && !showDays ? days * 3600 * 24 : 0

  let mnts = Math.floor(currentSecs / 60)

  mnts += hrs && !showHours ? hrs * 60 : 0
  currentSecs -= mnts * 60
  currentSecs += hrs && !showHours ? hrs * 3600 : 0

  currentSecs += mnts && !showMinutes ? mnts * 60 : 0

  if (!showSeconds) {
    mnts += Math.ceil(currentSecs / 60)
    if (mnts >= 60) {
      hrs += 1
      mnts = 0
    }
    currentSecs = 0
  }

  return {
    days,
    hours: hrs,
    minutes: mnts,
    seconds: currentSecs,
  }
}

function makeDurationStringFromSeconds(seconds, outputFormat) {
  if (!isNumber(seconds)) {
    return DEFAULT_WHEN_NO_DATA_AVAILABLE
  }
  const { showDays, showHours, showMinutes, showSeconds } = splitOutputFormat(outputFormat)
  const { days, hours, minutes, seconds: secs } = splitDuration(seconds, { showDays, showHours, showMinutes, showSeconds })

  let formattedTime = ''
  const daysLabel = days > 1 ? i18n.t('COMMON.DAYS') : i18n.t('COMMON.DAY')

  formattedTime += showDays && days ? `${days} ${daysLabel.toLowerCase()} ` : ''
  formattedTime += showHours && hours ? `${hours}h ` : ''
  formattedTime += showMinutes && minutes ? `${minutes}min ` : ''
  formattedTime += showSeconds && secs ? `${secs}${i18n.t('COMMON.SECONDS')}` : ''

  if (!days && !hours && !minutes && !secs) {
    return showSeconds ? '0sec' : '0min'
  }

  return formattedTime.trim()
}

/**
 * Get the person age using his birth date
 *
 * @returns {number}
 */
function getAge(date) {
  const currentDate = new Date()
  const birthDate = new Date(date)
  let age = currentDate.getUTCFullYear() - birthDate.getUTCFullYear()
  const month = currentDate.getMonth() - birthDate.getMonth()

  if (month < 0 || (month === 0 && currentDate.getDate() < birthDate.getDate())) {
    age -= 1
  }

  return age
}

/**
 * Format a date string or Date instance so it can be used
 * by a input[type='date']
 *
 * @param {string | Date} date
 */
function formatDateForInput(date) {
  if (!date) {
    return null
  }

  let d = date

  if (!date.getDate) {
    d = moment(date)
  }

  const day = d.date()
  const month = d.month() + 1

  const dd = day >= 10 ? day : `0${day}`
  const mm = month >= 10 ? month : `0${month}`
  const yyyy = d.year()

  return `${yyyy}-${mm}-${dd}`
}

const entryPoint = (reference, type, formatConfig) => {

  /* Shorthand for simple output manipulations (use ensuring the input format is correct).
     * Maintain old implementations support
     * i.e. 'YYYY-MM-DD' instead of {output: 'YYYY-MM-DD'}
     */

  let formatObj = formatConfig

  if (!(typeof formatConfig === 'object')) {
    formatObj = {
      input: DEFAULT_INPUT_DATE_FORMAT,
      output: formatObj,
      utc: false,
    }
  }

  let date

  switch (type) {
    case 'duration':
      return makeDurationStringFromSeconds(reference, formatObj.output)
    case 'date':
      date = makeDate(reference, formatObj.input, formatObj.utc)

      return makeDateString(date, formatObj.output)
    case 'age':
      date = makeDate(reference, formatObj.input)

      return getAge(date)
    case 'date-input':
      return formatDateForInput(reference)
    default:
      return 'Invalid filter type'
  }
}

Vue.filter('shDate', entryPoint)

export default entryPoint
